/**
 * Header scroll-collapse, ported from src/components/Header.tsx.
 *
 * The bar collapses when a [data-shop-trigger] element crosses the nav line on
 * a downward scroll, and reappears on a clear upward scroll or when the trigger
 * is pulled back well below the line. The lock and hysteresis stop it
 * oscillating when a scroll ends right on the boundary.
 */
(function () {
  const bar = document.querySelector('[data-cs-header-bar]');
  if (!bar) return;

  /**
   * Horizon scrolls the page inside .page-wrapper, not the window — it sets
   * html/body to overflow:hidden and gives the wrapper overflow-y:auto so the
   * theme drawer can slide the page sideways. Scroll events fire on that
   * element and do not bubble to window, so a window listener never fires.
   */
  const scroller = document.querySelector('.page-wrapper') || window;
  const scrollY = () => (scroller === window ? window.scrollY : scroller.scrollTop);

  const LOCK_MS = 350;
  const HYSTERESIS = 24;
  const NAV_BOTTOM = 80;

  let lastY = scrollY();
  let ticking = false;
  let lockedUntil = 0;
  let collapsed = false;

  const setCollapsed = (next) => {
    collapsed = next;
    bar.classList.toggle('h-0', next);
    bar.classList.toggle('opacity-0', next);
    bar.classList.toggle('pointer-events-none', next);
    bar.classList.toggle('h-20', !next);
    bar.classList.toggle('opacity-100', !next);
    document.documentElement.style.setProperty('--header-offset', next ? '0px' : '80px');
  };

  const update = () => {
    ticking = false;
    const now = performance.now();
    const y = scrollY();
    if (now < lockedUntil) {
      lastY = y;
      return;
    }
    const goingUp = y < lastY - 4;
    const trigger = document.querySelector('[data-shop-trigger]');
    const triggerTop = trigger ? trigger.getBoundingClientRect().top : Infinity;

    if (!collapsed) {
      if (trigger && y > lastY + 4 && triggerTop <= NAV_BOTTOM) {
        setCollapsed(true);
        lockedUntil = now + LOCK_MS;
      }
    } else if (y < 8 || goingUp || triggerTop > NAV_BOTTOM + HYSTERESIS) {
      setCollapsed(false);
      lockedUntil = now + LOCK_MS;
    }
    lastY = y;
  };

  scroller.addEventListener('scroll', () => {
    if (!ticking) {
      ticking = true;
      window.requestAnimationFrame(update);
    }
  }, { passive: true });

  /**
   * Mobile menu.
   *
   * It is a full-screen panel now, so the page behind it has to stop scrolling
   * — and the scroller is .page-wrapper, not the body: Horizon sets html/body
   * to overflow:hidden already and does its scrolling there, which is the same
   * reason the collapse listener above is bound to that element.
   */
  const toggle = document.querySelector('[data-cs-menu-toggle]');
  const nav = document.querySelector('[data-cs-mobile-nav]');

  if (toggle && nav) {
    const setOpen = (open) => {
      nav.classList.toggle('hidden', !open);
      toggle.setAttribute('aria-expanded', String(open));
      toggle.querySelector('[data-cs-menu-open]').classList.toggle('hidden', open);
      toggle.querySelector('[data-cs-menu-close]').classList.toggle('hidden', !open);

      const pageScroller = document.querySelector('.page-wrapper');
      if (pageScroller) pageScroller.style.overflow = open ? 'hidden' : '';

      /*
       * Lift the menu over Shopify's cookie banner, which is fixed at z-index
       * 2000000 and was covering it outright on a first visit.
       *
       * No z-index on the panel itself can do it. <header> is sticky at z-50 and
       * so is Horizon's header-group wrapper above it, and each is therefore its
       * own stacking context — the panel can only ever stack inside them. Both
       * have to come up, and with priority set: Tailwind's utilities are marked
       * !important in this build (tailwind.config.ts explains why), so a plain
       * inline z-index loses to the z-50 class.
       *
       * Restored on close so the banner is reachable again.
       */
      const header = document.querySelector('.cs-header');
      const headerGroup = header?.closest('.shopify-section')?.parentElement;

      for (const el of [header, headerGroup]) {
        if (!el) continue;
        if (open) el.style.setProperty('z-index', '2000001', 'important');
        else el.style.removeProperty('z-index');
      }

      if (open) nav.scrollTop = 0;
    };

    toggle.addEventListener('click', () => setOpen(nav.classList.contains('hidden')));

    nav.querySelectorAll('[data-cs-menu-dismiss]').forEach((el) => {
      el.addEventListener('click', () => setOpen(false));
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !nav.classList.contains('hidden')) setOpen(false);
    });

    // A link inside the panel navigates, but a same-page anchor would not —
    // leaving the menu covering whatever it jumped to.
    nav.querySelectorAll('a[href^="#"]').forEach((link) => {
      link.addEventListener('click', () => setOpen(false));
    });
  }

  document.documentElement.style.setProperty('--header-offset', '80px');
})();
